import React from 'react'
import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { STAGES, type StageState } from '@/lib/dealStages'

/**
 * The five-stage bar every deal shows at the top: Sent → Agreed → Do the work → Complete → Paid.
 * The current stage is highlighted; a declined or expired deal shows where it stopped.
 */
const DealStageBar: React.FC<{ state: StageState; className?: string }> = ({
  state,
  className,
}) => (
  <ol className={cn('flex items-start gap-1', className)} aria-label="Deal progress">
    {STAGES.map((s, i) => {
      const done = i < state.index || state.stage === 'paid'
      const current = i === state.index && state.stage !== 'paid'
      const stopped = state.ended && i === state.index
      return (
        <li key={s.id} className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
          <div className="flex w-full items-center">
            <span
              className={cn(
                'h-1 flex-1 rounded-full',
                i === 0 ? 'opacity-0' : done || current ? 'bg-[#C0FD72]' : 'bg-border',
              )}
            />
            <span
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold',
                stopped
                  ? 'border-[#FBC4C4] bg-[#FEECEC] text-[#B91C1C]'
                  : done
                    ? 'border-[#C0FD72] bg-[#C0FD72] text-[#0F172A]'
                    : current
                      ? 'border-[#C0FD72] bg-[#F7FEE7] text-[#2F5E06]'
                      : 'border-border text-muted-foreground',
              )}
            >
              {stopped ? (
                <X className="h-3.5 w-3.5" strokeWidth={3} />
              ) : done ? (
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
              ) : (
                i + 1
              )}
            </span>
            <span
              className={cn(
                'h-1 flex-1 rounded-full',
                i === STAGES.length - 1 ? 'opacity-0' : done ? 'bg-[#C0FD72]' : 'bg-border',
              )}
            />
          </div>
          <span
            className={cn(
              'max-w-full truncate text-center text-xs font-semibold',
              current || stopped
                ? 'text-foreground'
                : done
                  ? 'text-foreground'
                  : 'text-muted-foreground',
            )}
          >
            {s.label}
          </span>
        </li>
      )
    })}
  </ol>
)

export default DealStageBar
